/** @jsxFrag jsx */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import styled from "@emotion/styled";
import clsx from "clsx";
import {
  Checkbox,
  IconButton,
  TooltipHost,
  TooltipOverflowMode
} from "office-ui-fabric-react";
import { Fragment, useContext, useRef, useState } from "react";
import { DraggableProvided, DraggableStateSnapshot } from "react-beautiful-dnd";
import ReactDOM from "react-dom";
import { ColumnOptionsContext } from "./ColumnOptions";
import { FilterDropdown } from "./FilterDropdown";
import { Column } from "./types";
import { isColumnFiltered } from "./utils";

type Props = {
  column: Column;
  draggableProvided?: DraggableProvided;
  draggableStateSnapshot?: DraggableStateSnapshot;
};

export const ColumnListItem = ({
  column,
  draggableProvided,
  draggableStateSnapshot
}: Props) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const refInnerContainer = useRef<HTMLDivElement>(null);
  const { onToggleColumnVisibility, strings } = useContext(
    ColumnOptionsContext
  );

  const isFiltered = column.filter && isColumnFiltered(column.filter);

  let width = 0;
  if (refInnerContainer.current) {
    width = refInnerContainer.current.getBoundingClientRect().width;
  }

  const render = (
    <Root
      ref={draggableProvided?.innerRef}
      {...draggableProvided?.draggableProps}
      style={{
        ...draggableProvided?.draggableProps.style,
        zIndex: 99999999999999
      }}
    >
      <InnerContainer
        ref={refInnerContainer}
        className={clsx({
          isActive: isDropdownOpen || draggableStateSnapshot?.isDragging
        })}
      >
        <IconButton
          iconProps={{
            iconName: "GripperDotsVertical"
          }}
          title={strings.dragHandleTitle}
          css={css`
            cursor: grab;
            &.is-disabled {
              background: transparent;
            }
          `}
          disabled={column.isFixed}
          {...draggableProvided?.dragHandleProps}
        />
        <TooltipHost
          overflowMode={TooltipOverflowMode.Self}
          content={column.label}
          styles={{
            root: {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,

              width: "100%"
            }
          }}
        >
          {column.label}
        </TooltipHost>

        {!!column.filter && (
          <div>
            <IconButton
              iconProps={{
                iconName: isFiltered ? "FilterSolid" : "Filter"
              }}
              onMouseDown={() => {
                if (!isDropdownOpen) {
                  if (column.onOpenCallout) column.onOpenCallout();
                } else {
                  if (column.onDismissCallout) column.onDismissCallout();
                }
                setIsDropdownOpen(!isDropdownOpen);
              }}
            />
          </div>
        )}
        <Checkbox
          checked={column.isVisible}
          onChange={() => onToggleColumnVisibility(column.key)}
          title={strings.visibilityCheckboxTitle}
          disabled={column.isFixed}
        />
      </InnerContainer>
    </Root>
  );

  return (
    <Fragment>
      {draggableStateSnapshot?.isDragging
        ? ReactDOM.createPortal(render, document.body)
        : render}
      {isDropdownOpen && (
        <FilterDropdown
          column={column}
          width={width}
          isOpen={isDropdownOpen}
          close={() => {
            if (column.onDismissCallout) column.onDismissCallout();
            setIsDropdownOpen(false);
          }}
          target={refInnerContainer}
        />
      )}
    </Fragment>
  );
};

export const Root = styled.div`
  margin-bottom: 5px;
  /* position: relative; */
  z-index: 999999999;
`;

export const InnerContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  &.isActive {
    background: ${p => p.theme.palette.neutralLighter};
  }
`;

export const Label = styled.div`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  color: ${p => p.theme.palette.neutralPrimary};
`;
